/**
 * Express/Connect 兼容层
 * 让 middleware 同时支持 Express 和 Vite/Connect
 */
import type { ServerResponse, IncomingMessage } from 'http';

type AnyResponse = ServerResponse & {
  status?: (code: number) => AnyResponse;
  json?: (data: unknown) => void;
  send?: (data: unknown) => void;
};

type AnyRequest = IncomingMessage & {
  query?: Record<string, string>;
  params?: Record<string, string>;
};

/**
 * 发送 JSON 响应，兼容 Express 和 Connect
 */
export function sendJson(res: AnyResponse, data: unknown, statusCode = 200): void {
  const statusFn = res.status;
  const jsonFn = res.json;
  if (typeof statusFn === 'function' && typeof jsonFn === 'function') {
    // Express style
    statusFn.call(res, statusCode);
    jsonFn.call(res, data);
  } else {
    // Connect/Node style
    res.statusCode = statusCode;
    res.setHeader('Content-Type', 'application/json');
    res.end(JSON.stringify(data));
  }
}

/**
 * 发送错误响应
 */
export function sendError(
  res: AnyResponse,
  message: string,
  error?: unknown,
  statusCode = 500
): void {
  const errorData = {
    message,
    ...(error ? { error: serializeError(error) } : {}),
  };
  sendJson(res, errorData, statusCode);
}

/**
 * 发送成功响应
 */
export function sendSuccess(res: AnyResponse, data?: Record<string, unknown>): void {
  sendJson(res, { success: true, ...data }, 200);
}

/**
 * 获取 query 参数，兼容 Express 和 Connect
 */
export function getQuery(req: AnyRequest): Record<string, string> {
  if (req.query) {
    return req.query as Record<string, string>;
  }
  // Connect/Node style - parse from URL
  const url = new URL(req.url || '', `http://${req.headers.host}`);
  return Object.fromEntries(url.searchParams.entries());
}

/**
 * 获取单个 query 参数
 */
export function getQueryParam(req: AnyRequest, key: string): string | undefined {
  const query = getQuery(req);
  return query[key];
}

/**
 * 序列化错误对象
 */
function serializeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }
  return { message: String(error) };
}
